import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["bookmarkButton", "userDropdown", "userAvatar", "signInOut", "bookmarksList"]
  static values = {
    refreshing: Boolean
  }

  connect() {
    // Initialize the registry if it doesn't exist
    if (!window.userControllerRegistry) {
      window.userControllerRegistry = {
        controllers: [],
        refreshing: false,
        lastRefreshed: 0,
        data: null
      }
    }

    // Register this controller instance for global access
    window.userControllerRegistry.controllers.push(this)

    // Fetch user info
    this.fetchUserInfo()
  }

  disconnect() {
    // Remove this controller instance when disconnected
    if (window.userControllerRegistry) {
      window.userControllerRegistry.controllers =
        window.userControllerRegistry.controllers.filter(controller => controller !== this)
    }
  }

  async fetchUserInfo(forceRefresh = false) {
    // Ensure registry exists
    if (!window.userControllerRegistry) {
      window.userControllerRegistry = {
        controllers: [],
        refreshing: false,
        lastRefreshed: 0,
        data: null
      }
    }

    const registry = window.userControllerRegistry
    const now = Date.now()

    // If we're already refreshing, don't start another request
    if (registry.refreshing) return

    // If we've fetched data recently (within 1 second) and not forcing refresh, use cached data
    if (!forceRefresh && registry.data && (now - registry.lastRefreshed < 1000)) {
      this.processUserData(registry.data)
      return
    }

    // Mark as refreshing
    registry.refreshing = true

    try {
      const response = await fetch('/user-info')
      const data = await response.json()

      // Update registry with new data and timestamp
      registry.data = data
      registry.lastRefreshed = now

      // Process data for this controller
      this.processUserData(data)

      // Update all other controllers without triggering new API calls
      this.updateOtherControllers()
    } catch (error) {
      console.error('Error fetching user info:', error)
      this.showUnauthenticatedState()
    } finally {
      registry.refreshing = false
    }
  }

  // Process user data for this controller instance
  processUserData(data) {
    if (data.authenticated) {
      this.updateUserInterface(data.user)
      this.updateAllBookmarks(data.bookmarks)
    } else {
      google.accounts.id.prompt(); // This will trigger the Google One Tap prompt
      this.showUnauthenticatedState()
    }
  }

  // Update other controllers with already fetched data
  updateOtherControllers() {
    const registry = window.userControllerRegistry
    if (registry && registry.controllers.length > 0) {
      registry.controllers.forEach(controller => {
        if (controller !== this && registry.data) {
          controller.processUserData(registry.data)
        }
      })
    }
  }

  updateUserInterface(user) {
    // Update user avatar
    if (this.hasUserAvatarTarget) {
      if (user.avatar) {
        this.userAvatarTarget.innerHTML = `<img src="${user.avatar}" class="h-full w-full rounded-full" alt="${user.name || 'User avatar'}">`
      } else {
        this.userAvatarTarget.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        `
      }
    }

    // Update sign in/out button
    if (this.hasSignInOutTarget) {
      // Conditionally render the "Admin" link for non-'is_user' roles
      let adminLink = '';
      if (user.role !== 'is_user') {
        adminLink = `<a href="/admin" class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-center">Admin</a>`;
      }
      this.signInOutTarget.innerHTML = `
        <form action="/session" method="post" class="w-full">
          <input type="hidden" name="_method" value="delete">
          ${adminLink}
          <button type="submit" class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Sign Out</button>
        </form>
      `
    }
  }

  // Update all bookmark UI elements across the page
  updateAllBookmarks(bookmarks) {
    this.updateBookmarkButtons(bookmarks)
    this.updateBookmarksList(bookmarks)
  }

  updateBookmarkButtons(bookmarks) {
    // Find all bookmark buttons on the page
    document.querySelectorAll('[id^="bookmark-button-container-"]').forEach(container => {
      // Extract hotel ID from container ID
      const hotelId = container.id.split('-').pop()
      const bookmarkableType = container.dataset.bookmarkableType
      const isBookmarked = bookmarks.some(bookmark => bookmark.id === parseInt(hotelId) && bookmark.bookmarkable_type === bookmarkableType)

      // Clear existing content and create new button
      container.innerHTML = ''

      const bookmarkLink = document.createElement('a')
      bookmarkLink.href = 'javascript:void(0)'
      bookmarkLink.className = 'flex items-center gap-2'
      bookmarkLink.setAttribute('data-hotel-id', hotelId)
      bookmarkLink.setAttribute('data-bookmarkable-type', bookmarkableType)
      bookmarkLink.dataset.action = isBookmarked ? 'click->user#removeBookmark' : 'click->user#addBookmark'

      bookmarkLink.innerHTML = `
        <div id="bookmark-button-${hotelId}">
          <svg viewBox="0 0 24 24" height="18" width="16" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 21V3h14v18l-7-3${isBookmarked ? '' : 'zm2-3.05 5-2.15 5 2.15V5H7z'}" fill="#777E90"></path>
          </svg>
        </div>
      `

      container.appendChild(bookmarkLink)
    })
  }

  updateBookmarksList(bookmarks) {
    if (!this.hasBookmarksListTarget) return;

    if (!bookmarks || bookmarks.length === 0) {
      this.bookmarksListTarget.innerHTML = `
        <div class="py-6 px-4 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <p class="text-gray-500">You haven't saved any hotels yet</p>
        </div>
      `;
      return;
    }

    const bookmarkItems = bookmarks.map(bookmark => `
      <div id="bookmark-item-${bookmark.id}" class="p-4 border-b border-gray-100 flex items-start bookmark-item">
        <div class="flex-shrink-0 w-16 h-16 mr-3">
          ${bookmark.image_url ? `
            <img src="${bookmark.image_url}" alt="${bookmark.name}" class="w-full h-full object-cover rounded">
          ` : `
            <div class="w-full h-full bg-gray-200 rounded flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3.5L19 21z" />
              </svg>
            </div>
          `}
        </div>

        <div class="flex-grow">
          <a href="${bookmark.url}" class="block hover:underline">
            <h4 class="font-medium text-gray-900 text-sm line-clamp-2">${bookmark.name}</h4>
          </a>
          ${bookmark.base_price ? `
            <p class="text-gray-600 text-sm mt-1">
              <span data-original-price="${bookmark.base_price}">
                $${bookmark.base_price}
              </span> /day
            </p>
          ` : ''}
          <p class="text-gray-900 text-sm mt-1">${bookmark.bookmarkable_type == "CulturalSouvenir" ? "Cultural Souvenir" : "Hotel"}</p>
        </div>

        <a href="javascript:void(0)"
           class="ml-2 text-gray-400 hover:text-gray-600 bookmark-delete"
           data-action="click->user#removeBookmark"
           data-user-hotel-id-param="${bookmark.id}"
           data-dropdown-ignore-close="true">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </a>
      </div>
    `).join('');

    this.bookmarksListTarget.innerHTML = bookmarkItems;
  }

  async addBookmark(event) {
    event.preventDefault();
    const hotelId = event.currentTarget.dataset.hotelId;

    if (!hotelId) return;

    try {
      const response = await fetch(`/api/v1/bookmarks`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookmarkable_id: hotelId, bookmarkable_type: event.currentTarget.dataset.bookmarkableType })
      });

      if (response.ok) {
        // Force refresh after bookmark action
        this.fetchUserInfo(true);
      } else {
        console.error('Failed to add bookmark:', response.statusText);
      }
    } catch (error) {
      console.error('Error adding bookmark:', error);
    }
  }

  async removeBookmark(event) {
    event.preventDefault();
    // Check if the event came from bookmark list or hotel page
    const hotelId = event.currentTarget.dataset.userHotelIdParam || event.currentTarget.dataset.hotelId;

    if (!hotelId) return;

    try {
      const response = await fetch(`/api/v1/bookmarks/${hotelId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Force refresh after bookmark action
        this.fetchUserInfo(true);
      } else {
        console.error('Failed to remove bookmark:', response.statusText);
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
    }
  }

  showUnauthenticatedState() {
    // Update user avatar to default state
    if (this.hasUserAvatarTarget) {
      this.userAvatarTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      `
    }

    // Update sign in/out link
    if (this.hasSignInOutTarget) {
      this.signInOutTarget.innerHTML = `
        <a href="/session/new" class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Sign In</a>
      `
    }

    // Update bookmarks list to show sign in message
    if (this.hasBookmarksListTarget) {
      this.bookmarksListTarget.innerHTML = `
        <div class="py-6 px-4 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <p class="text-gray-500">Sign in to view your saved hotels</p>
        </div>
      `;
    }

    // Update all bookmark buttons to redirect to sign in
    document.querySelectorAll('[id^="bookmark-button-container-"]').forEach(container => {
      container.innerHTML = `
        <a href="/session/new" class="flex items-center gap-2">
          <div>
            <svg viewBox="0 0 24 24" height="18" width="16" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 21V3h14v18l-7-3zm2-3.05 5-2.15 5 2.15V5H7z" fill="#777E90"></path>
            </svg>
          </div>
        </a>
      `;
    });
  }
}